import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { connect, useDispatch } from 'react-redux';
import { Modal, Dropdown, Checkbox, Rate, Tooltip } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Mail, Calendar, Type, ChevronDown, Flag, Hash, DollarSign, LayoutList,
  Star, Phone, AlignLeft, FileText, CheckSquare, Users, Calculator,
  UserRound, MapPin, Clock, Banknote, Percent, Timer, Binary,
  Globe, Palette, Paperclip, Camera, PenLine, User, Link2, CircleDot,
} from 'lucide-react';
import Loader from '../../atoms/global/Loader';
import { fieldTypes, COLOUR_PALETTE } from '../../../../js/constants';
import { getRandomColor, getTagColor, getProjectColourPalette } from '../../../../js/helper';
import {
  toggleWorkspaceFieldsModal,
  getCustomFieldsByAI,
  deleteWorkspaceCustomField,
  addExistingCustomFieldToProject,
  promoteFieldToWorkspace,
  getProjectById,
  addCustomFieldToProject,
  updateProjectCustomField,
  deleteProjectCustomField,
} from '../../../../actions';

const FIELD_ICONS = {
  'status':       CircleDot,
  'link':         Link2,
  'date':         Calendar,
  'text':         Type,
  'dropdown':     ChevronDown,
  'priority':     Flag,
  'number':       Hash,
  'cost':         DollarSign,
  'multi-select': LayoutList,
  'email':        Mail,
  'rating':       Star,
  'phone':        Phone,
  'long text':    AlignLeft,
  'rich text':    FileText,
  'checkbox':     CheckSquare,
  'people':       Users,
  'formula':      Calculator,
  'full-name':    UserRound,
  'address':      MapPin,
  'datetime':     Clock,
  'currency':     Banknote,
  'percentage':   Percent,
  'duration':     Timer,
  'id-reference': Binary,
  'country':      Globe,
  'color':        Palette,
  'file-upload':  Paperclip,
  'camera':       Camera,
  'signature':    PenLine,
  'assignee':     User,
};

const FIELD_BADGE_STYLES = {
  'status':       { bg: 'rgba(124,58,216,0.1)',  border: 'rgba(124,58,216,0.18)',  color: '#7c3aed' },
  'link':         { bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.18)',  color: '#f97316' },
  'date':         { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.18)',   color: '#ef4444' },
  'text':         { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#14b8a6' },
  'dropdown':     { bg: 'rgba(236,72,153,0.1)',  border: 'rgba(236,72,153,0.18)',  color: '#ec4899' },
  'priority':     { bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.18)',  color: '#f97316' },
  'number':       { bg: 'rgba(244,63,94,0.1)',   border: 'rgba(244,63,94,0.18)',   color: '#f43f5e' },
  'cost':         { bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.18)',  color: '#10b981' },
  'multi-select': { bg: 'rgba(217,70,239,0.1)',  border: 'rgba(217,70,239,0.18)',  color: '#d946ef' },
  'email':        { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.18)',  color: '#3b82f6' },
  'rating':       { bg: 'rgba(234,179,8,0.1)',   border: 'rgba(234,179,8,0.18)',   color: '#ca8a04' },
  'phone':        { bg: 'rgba(14,165,233,0.1)',  border: 'rgba(14,165,233,0.18)',  color: '#0ea5e9' },
  'long text':    { bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.18)',   color: '#16a34a' },
  'rich text':    { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0d9488' },
  'checkbox':     { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.18)',  color: '#2563eb' },
  'people':       { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#0891b2' },
  'formula':      { bg: 'rgba(99,102,241,0.1)',  border: 'rgba(99,102,241,0.18)',  color: '#4f46e5' },
  'full-name':    { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#06b6d4' },
  'address':      { bg: 'rgba(251,146,60,0.1)',  border: 'rgba(251,146,60,0.18)',  color: '#ea580c' },
  'datetime':     { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#9333ea' },
  'currency':     { bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.18)',  color: '#d97706' },
  'percentage':   { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#7c3aed' },
  'duration':     { bg: 'rgba(6,182,212,0.1)',   border: 'rgba(6,182,212,0.18)',   color: '#0891b2' },
  'id-reference': { bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.18)', color: '#475569' },
  'country':      { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0f766e' },
  'color':        { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.18)',   color: '#dc2626' },
  'file-upload':  { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#0d9488' },
  'camera':       { bg: 'rgba(168,85,247,0.1)',  border: 'rgba(168,85,247,0.18)',  color: '#9333ea' },
  'signature':    { bg: 'rgba(20,184,166,0.1)',  border: 'rgba(20,184,166,0.18)',  color: '#14b8a6' },
  'assignee':     { bg: 'rgba(14,165,233,0.1)',  border: 'rgba(14,165,233,0.18)',  color: '#0ea5e9' },
};

// ─── Drag-and-drop option row ─────────────────────────────────────────────────
const OPTION_SWATCHES = COLOUR_PALETTE;

const ColorPickerPortal = ({ anchorRef, color, onSelect, onClose }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left - 4 });
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e) => { if (!anchorRef.current?.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return createPortal(
    <div
      className="p-2 rounded-xl shadow-2xl"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, background: 'white', border: '1px solid rgba(99,102,241,0.15)', width: 152 }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="grid grid-cols-5 gap-1.5">
        {OPTION_SWATCHES.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => { onSelect(c); onClose(); }}
            className="w-5 h-5 rounded-full transition-transform hover:scale-125"
            style={{ background: c, outline: color === c ? `2px solid ${c}` : 'none', outlineOffset: 2 }}
          />
        ))}
      </div>
    </div>,
    document.body,
  );
};

const OptionRow = React.memo(({ option, index, draggingId, onLabelChange, onColorChange, onDelete }) => {
  const color = getTagColor(option.metadata?.color) || option.metadata?.color || '#ccc';
  const draggableId = String(option?.id ?? option?._id ?? option?.key ?? `${option?.label}-${index}`);
  const [local, setLocal] = useState(option.label || '');
  const [showPicker, setShowPicker] = useState(false);
  const dotRef = useRef(null);

  useEffect(() => { setLocal(option.label || ''); }, [option.label]);

  const commit = useCallback(() => {
    const v = local.trim();
    if (!v) onDelete(index);
    else onLabelChange(index, v);
  }, [local, index, onLabelChange, onDelete]);

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="draggable-customfield-row flex items-center gap-2 px-3 py-1.5"
          style={{
            ...provided.draggableProps.style,
            background: snapshot.isDragging ? 'rgba(99,102,241,0.09)' : 'transparent',
            borderRadius: snapshot.isDragging ? 8 : 0,
            boxShadow: snapshot.isDragging ? '0 4px 16px rgba(99,102,241,0.18)' : 'none',
            border: snapshot.isDragging ? '1px solid rgba(99,102,241,0.3)' : 'none',
          }}
        >
          <div
            className={`customfield-options-row-drag-indicator${snapshot.isDragging ? ' isDragging' : draggingId && draggingId !== draggableId ? '' : ' hover-drag-icon'}`}
            {...provided.dragHandleProps}
          >
            <DragIndicatorIcon style={{ fontSize: 16, color: 'rgba(99,102,241,0.35)' }} />
          </div>
          <button
            ref={dotRef}
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => setShowPicker(p => !p)}
            className="w-3.5 h-3.5 rounded-full flex-shrink-0 hover:scale-125 transition-transform"
            style={{ background: color, boxShadow: showPicker ? `0 0 0 3px ${color}44` : undefined }}
            title="Change color"
          />
          {showPicker && (
            <ColorPickerPortal
              anchorRef={dotRef}
              color={color}
              onSelect={(c) => onColorChange(index, c)}
              onClose={() => setShowPicker(false)}
            />
          )}
          <input
            className="flex-1 text-sm bg-transparent py-0.5 px-1 border-b border-transparent focus:outline-none transition-colors"
            style={{ color: 'rgba(15,15,25,0.75)', borderBottomColor: 'transparent' }}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={commit}
            onFocus={e => e.target.style.borderBottomColor = 'rgba(99,102,241,0.4)'}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
          />
          <button
            type="button"
            onClick={() => onDelete(index)}
            className="flex-shrink-0 p-0.5 rounded transition-colors"
            style={{ color: 'rgba(239,68,68,0.6)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.07)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <img src="/images/icons/delete-red.svg" width={14} height={14} alt="" />
          </button>
        </div>
      )}
    </Draggable>
  );
});

// ─── Reusable options editor ───────────────────────────────────────────────────
const OptionsEditor = ({ options, setOptions, intl }) => {
  const [input, setInput] = useState('');
  const [draggingId, setDraggingId] = useState(null);

  const withColors = useMemo(
    () => options.map((o, i) => ({ ...o, metadata: { ...o.metadata, color: o.metadata?.color || getRandomColor(i) } })),
    [options],
  );

  useEffect(() => {
    const needs = options.some((o) => !o.metadata?.color);
    if (needs) {
      setOptions((prev) =>
        prev.map((o, i) => o.metadata?.color ? o : { ...o, metadata: { ...o.metadata, color: getRandomColor(i) } }),
      );
    }
  }, [options.length]);

  const add = () => {
    if (!input.trim()) return;
    setOptions((prev) => [
      ...prev,
      { key: Math.random(), id: `opt-${Date.now()}`, label: input.trim(), metadata: { color: getRandomColor(prev.length) } },
    ]);
    setInput('');
  };

  return (
    <div>
      <div className="rounded-xl overflow-hidden mb-2" style={{ border: '1px solid rgba(99,102,241,0.15)', background: 'white' }}>
        <DragDropContext
          onDragStart={(s) => setDraggingId(s.draggableId)}
          onDragEnd={(r) => {
            setDraggingId(null);
            if (!r.destination || r.destination.index === r.source.index) return;
            setOptions((prev) => {
              const arr = [...prev];
              const [m] = arr.splice(r.source.index, 1);
              arr.splice(r.destination.index, 0, m);
              return arr;
            });
          }}
        >
          {/* Scroll wrapper must be OUTSIDE Droppable — rbd requires droppable div to not be the scrolling element */}
          <div className="max-h-52 overflow-y-auto divide-y" style={{ borderColor: 'rgba(99,102,241,0.06)' }}>
            <Droppable droppableId="opts">
              {(p) => (
                <div ref={p.innerRef} {...p.droppableProps}>
                  {withColors.map((opt, i) => (
                    <OptionRow
                      key={opt?.id ?? opt?._id ?? opt?.key ?? `${opt?.label}-${i}`}
                      option={opt}
                      index={i}
                      draggingId={draggingId}
                      onLabelChange={(idx, val) => setOptions((prev) => { const n = [...prev]; n[idx] = { ...n[idx], label: val }; return n; })}
                      onColorChange={(idx, c) => setOptions((prev) => { const n = [...prev]; n[idx] = { ...n[idx], metadata: { ...n[idx].metadata, color: c } }; return n; })}
                      onDelete={(idx) => setOptions((prev) => { const n = [...prev]; n.splice(idx, 1); return n; })}
                    />
                  ))}
                  {p.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onBlur={add}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={`${intl.formatMessage({ id: 'INPUT_OPTION', defaultMessage: 'Add option' })} ${options.length + 1}`}
          className="flex-1 h-9 px-3 text-sm rounded-lg bg-white focus:outline-none transition-colors"
          style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.75)' }}
          onFocus={e => e.target.style.borderColor = 'rgba(99,102,241,0.5)'}
          onBlurCapture={e => e.target.style.borderColor = 'rgba(99,102,241,0.2)'}
        />
        <button
          type="button"
          onClick={add}
          className="h-9 px-4 text-white text-sm font-semibold rounded-lg transition-colors"
          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          +
        </button>
      </div>
    </div>
  );
};

// ─── Coloured field-type icon badge ────────────────────────────────────────────
const DEFAULT_BADGE = { bg: 'rgba(99,102,241,0.1)', border: 'rgba(99,102,241,0.18)', color: '#4f46e5' };

const TypeBadge = ({ ftype, size = 'md' }) => {
  const ft = fieldTypes.find((f) => f.key === ftype?.key) || ftype || {};
  const dim = size === 'sm' ? 'w-6 h-6' : size === 'lg' ? 'w-10 h-10' : 'w-8 h-8';
  const iconSize = size === 'sm' ? 12 : size === 'lg' ? 20 : 15;
  const Icon = FIELD_ICONS[ft.key] || Hash;
  const s = FIELD_BADGE_STYLES[ft.key] || DEFAULT_BADGE;
  return (
    <div className={`${dim} rounded-lg flex items-center justify-center flex-shrink-0`} style={{ background: s.bg, border: `1px solid ${s.border}` }}>
      <Icon size={iconSize} color={s.color} strokeWidth={1.5} />
    </div>
  );
};

// ─── Panel header row with optional back button ─────────────────────────────────
const PanelHeader = ({ onBack, title, subtitle, badge }) => (
  <div className="flex items-center gap-3 px-5 py-3.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(99,102,241,0.1)', background: 'white' }}>
    {onBack && (
      <button
        type="button"
        onClick={onBack}
        className="p-1.5 rounded-lg transition-colors"
        style={{ color: 'rgba(99,102,241,0.6)' }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.07)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <img src="/images/icons/chevron-left.svg" className="size-4" alt="back" />
      </button>
    )}
    {badge && badge}
    <div className="min-w-0">
      <div className="text-sm font-semibold leading-tight" style={{ color: 'rgba(15,15,25,0.85)' }}>{title}</div>
      {subtitle && <div className="text-xs mt-0.5" style={{ color: 'rgba(15,15,25,0.4)' }}>{subtitle}</div>}
    </div>
  </div>
);

// ─── Shared input style helper ─────────────────────────────────────────────────
const inputClass = "w-full h-10 px-3 text-sm rounded-xl bg-white focus:outline-none transition-colors";
const inputStyle = { border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.8)' };
const inputFocus = (e) => { e.target.style.borderColor = 'rgba(99,102,241,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.08)'; };
const inputBlur  = (e) => { e.target.style.borderColor = 'rgba(99,102,241,0.2)'; e.target.style.boxShadow = 'none'; };

const labelClass = "block text-[11px] font-semibold uppercase tracking-wider mb-1.5";
const labelStyle = { color: 'rgba(15,15,25,0.4)' };

// ─── Main modal ────────────────────────────────────────────────────────────────
const WorkspaceFieldsModal = (props) => {
  const { isModalOpen, workspace, fields, workspaceCustomFields, myProjects, onClose, onAddCustomFieldSuccess, modalProjectId, allowedProjectIds } = props;
  const availableProjects = allowedProjectIds?.length
    ? (myProjects || []).filter(p => allowedProjectIds.includes(p._id))
    : (myProjects || []);

  const intl = useIntl();
  const dispatch = useDispatch();
  const { 1: urlProjectId } = window.location.pathname.split('/');
  const userId = localStorage.getItem('Id');
  const contextProjectId = modalProjectId || urlProjectId;

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [mode, setMode] = useState(null);
  const [modeInitialized, setModeInitialized] = useState(false);

  const [newFieldType, setNewFieldType] = useState(null);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState([]);
  const [saveToWorkspace, setSaveToWorkspace] = useState(false);

  const [editField, setEditField] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('text');
  const [editOriginalType, setEditOriginalType] = useState('text');
  const [editTypeAcknowledged, setEditTypeAcknowledged] = useState(false);
  const [editOptions, setEditOptions] = useState([]);

  const [librarySearch, setLibrarySearch] = useState('');

  const [industry, setIndustry] = useState(workspace?.industry || workspace?.description || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addingFieldId, setAddingFieldId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [promoteConfirm, setPromoteConfirm] = useState(false);
  const [step1Search, setStep1Search] = useState('');
  const [step1Filter, setStep1Filter] = useState('all');
  const [libraryDeleteField, setLibraryDeleteField] = useState(null);

  const projectFields = selectedProject?.customFields || [];
  const currentUser = workspace?.members?.find((m) => m._id === userId);
  const isAdmin = ['owner', 'admin'].includes(currentUser?.role);

  const libraryFields = useMemo(
    () => (workspaceCustomFields || []).filter((wf) => !projectFields.find((pf) => pf._id === wf._id)),
    [workspaceCustomFields, projectFields],
  );

  const isSharedField = useCallback(
    (field) => (workspaceCustomFields || []).some((wf) => String(wf._id) === String(field?._id)),
    [workspaceCustomFields],
  );

  const getUsageCount = useCallback(
    (field) =>
      (myProjects || []).filter((p) =>
        (p.customFields || []).some((cf) => String(cf._id) === String(field?._id)),
      ).length,
    [myProjects],
  );

  useEffect(() => {
    if (!isModalOpen) return;
    setModeInitialized(false);
    setMode(null);
    setEditField(null);
  }, [isModalOpen, contextProjectId]);

  useEffect(() => {
    if (!myProjects?.length) return;
    if (!modeInitialized) {
      const matched = availableProjects.find((p) => p._id === contextProjectId) || null;
      setSelectedProject(matched);
      if (!matched) setMode('library');
      setModeInitialized(true);
    } else if (selectedProject) {
      // Keep selected project data fresh after store updates (e.g. after getProjectById)
      const refreshed = myProjects.find((p) => p._id === selectedProject._id);
      if (refreshed) setSelectedProject(refreshed);
    }
  }, [myProjects, contextProjectId]);

  useEffect(() => {
    if (workspace?.industry || workspace?.description) {
      setIndustry(workspace.industry || workspace.description);
    }
  }, [workspace?.industry, workspace?.description]);

  const closeModal = () => {
    setMode(null);
    setModeInitialized(false);
    setEditField(null);
    dispatch(toggleWorkspaceFieldsModal(false, ''));
    onClose?.();
  };

  const resetCreate = () => {
    setNewFieldType(null);
    setNewFieldName('');
    setNewFieldOptions([]);
    setSaveToWorkspace(false);
    setIsSubmitting(false);
  };

  const openCreate = (ftype, prefill = {}) => {
    setNewFieldType(ftype);
    setNewFieldName(prefill.name || '');
    setNewFieldOptions(prefill.possibleValues || ftype?.possibleValues || ftype?.options || []);
    setSaveToWorkspace(false);
    setMode('add-step2');
  };

  const openEdit = (field) => {
    setDeleteConfirm(false);
    setEditField(field);
    setEditName(field.name || '');
    setEditType(field.type || 'text');
    setEditOriginalType(field.type || 'text');
    setEditTypeAcknowledged(false);
    const raw = field.possibleValues || [];
    setEditOptions(raw.map((o, i) =>
      typeof o === 'string'
        ? { id: `opt-${i}`, label: o, metadata: { color: getRandomColor(i) } }
        : o
    ));
    setMode('edit');
  };

  const generateAI = () => {
    if (!industry.trim()) return;
    setIsLoading(true);
    dispatch(
      getCustomFieldsByAI(
        { industry, categories: ['essentials'], entityId: workspace?._id, entityType: 'workspace' },
        () => setIsLoading(false),
      ),
    );
  };

  const handleCreate = () => {
    if (!newFieldName.trim() || !selectedProject || isSubmitting) return;
    const requiresOpts = ['dropdown', 'status', 'multi-select'].includes(newFieldType?.key);
    if (requiresOpts && newFieldOptions.length === 0) return;

    const cleanOpts = newFieldOptions.map(({ key: _k, ...rest }) => rest);
    setIsSubmitting(true);
    dispatch(
      addCustomFieldToProject(
        {
          name: newFieldName.trim(),
          entityId: selectedProject._id,
          entityType: 'project',
          type: newFieldType?.key,
          possibleValues: cleanOpts,
          addToWorkspace: saveToWorkspace,
        },
        async (customField) => {
          onAddCustomFieldSuccess?.(customField);
          await dispatch(getProjectById(selectedProject._id));
          resetCreate();
          setMode(null);
        },
      ),
    );
  };

  const handleSave = () => {
    if (!editName.trim() || !selectedProject) return;
    const cleanOpts = editOptions.map(({ key: _k, ...rest }) => rest);
    dispatch(
      updateProjectCustomField(
        editField?._id,
        { name: editName.trim(), possibleValues: cleanOpts, projectId: selectedProject._id, type: editType },
        async () => {
          await dispatch(getProjectById(selectedProject._id));
          setMode(null);
          setEditField(null);
        },
      ),
    );
  };

  const handleDelete = () => {
    setDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    dispatch(
      deleteProjectCustomField(editField?._id, selectedProject?._id, async () => {
        await dispatch(getProjectById(selectedProject._id));
        setMode(null);
        setEditField(null);
        setDeleteConfirm(false);
      }, intl),
    );
  };

  const handlePromote = () => {
    if (!editField || !workspace?._id) return;
    dispatch(
      promoteFieldToWorkspace(editField._id, workspace._id, async () => {
        await dispatch(getProjectById(selectedProject._id));
      }),
    );
  };

  const handleAddFromLibrary = (field) => {
    if (!selectedProject || addingFieldId) return;
    setAddingFieldId(field._id);
    dispatch(
      addExistingCustomFieldToProject(
        { customFieldId: field._id, entityId: selectedProject._id, entityType: 'project' },
        async () => {
          await dispatch(getProjectById(selectedProject._id));
          onAddCustomFieldSuccess?.(field);
          setAddingFieldId(null);
        },
      ),
    );
  };

  // ── Right panel renders ───────────────────────────────────────────────────

  const renderWelcome = () => {
    if (!selectedProject) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-10">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)' }}>
            <img src="/images/icons/custom-fields.svg" style={{ height: 28, width: 28, opacity: 0.6 }} alt="" />
          </div>
          <div className="flex items-center gap-2 mb-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            <p className="text-sm font-semibold" style={{ color: 'rgba(15,15,25,0.75)' }}>Select a project to get started</p>
          </div>
          <p className="text-xs leading-relaxed max-w-[220px]" style={{ color: 'rgba(15,15,25,0.45)' }}>
            Choose a project from the left panel to view and manage its custom fields.
          </p>
          <div className="mt-5 w-full max-w-[260px]" style={{ borderTop: '1px solid rgba(99,102,241,0.1)' }}>
            <p className="text-xs mt-4 mb-2.5" style={{ color: 'rgba(15,15,25,0.4)' }}>
              Want to browse fields available across your workspace?
            </p>
            <button
              type="button"
              onClick={() => { setLibrarySearch(''); setMode('library'); }}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-all"
              style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.2)', color: '#4f46e5' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.13)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.07)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              Open Shared Fields Library
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full px-8 gap-3">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-1" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)' }}>
          <img src="/images/icons/custom-fields.svg" style={{ height: 28, width: 28, opacity: 0.6 }} alt="" />
        </div>
        <div className="text-center mb-1">
          <h3 className="text-sm font-semibold" style={{ color: 'rgba(15,15,25,0.8)' }}>Custom Fields — {selectedProject.title}</h3>
          <p className="text-xs mt-1" style={{ color: 'rgba(15,15,25,0.4)' }}>How would you like to add a field?</p>
        </div>

        {/* Option 1 — Shared Library (recommended) */}
        <button
          type="button"
          onClick={() => { setLibrarySearch(''); setMode('library'); }}
          className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-left transition-all"
          style={{ background: 'white', border: '1px solid rgba(15,15,25,0.12)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'; e.currentTarget.style.background = 'rgba(99,102,241,0.03)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,15,25,0.12)'; e.currentTarget.style.background = 'white'; }}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.08)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold" style={{ color: 'rgba(15,15,25,0.8)' }}>Browse Shared Fields</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>Recommended</span>
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: 'rgba(15,15,25,0.45)' }}>Reuse fields already set up in your workspace.</div>
          </div>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(15,15,25,0.25)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>

        {/* Option 3 — Add from scratch */}
        <button
          type="button"
          onClick={() => setMode('add-step1')}
          className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-left transition-all"
          style={{ background: 'white', border: '1px solid rgba(15,15,25,0.12)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(15,15,25,0.25)'; e.currentTarget.style.background = 'rgba(15,15,25,0.02)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,15,25,0.12)'; e.currentTarget.style.background = 'white'; }}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(15,15,25,0.04)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(15,15,25,0.4)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold" style={{ color: 'rgba(15,15,25,0.8)' }}>Create a new field</div>
            <div className="text-[11px] mt-0.5" style={{ color: 'rgba(15,15,25,0.45)' }}>Build a brand new field from scratch.</div>
          </div>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(15,15,25,0.25)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>

        {/* Edit hint */}
        {projectFields.length > 0 && (
          <div className="w-full flex items-center gap-2 px-1 pt-1">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(15,15,25,0.45)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            <p className="text-xs font-medium" style={{ color: 'rgba(15,15,25,0.55)' }}>
              To edit an existing field, click it in the left panel.
            </p>
          </div>
        )}
      </div>
    );
  };

  const STEP1_CATEGORIES = [
    { key: 'all',     label: 'All' },
    { key: 'choices', label: 'Choices & Status',   keys: ['dropdown', 'status', 'multi-select', 'priority', 'rating', 'checkbox'] },
    { key: 'text',    label: 'Text & Links',        keys: ['text', 'long text', 'rich text', 'link'] },
    { key: 'numeric', label: 'Numeric & Finance',   keys: ['number', 'cost', 'currency', 'percentage', 'formula', 'duration'] },
    { key: 'contact', label: 'Contact & People',    keys: ['email', 'phone', 'address', 'country', 'people', 'assignee', 'full-name'] },
    { key: 'date',    label: 'Date & Media',        keys: ['date', 'datetime', 'file-upload', 'camera', 'signature', 'color', 'id-reference'] },
  ];

  const renderStep1 = () => {
    const q = step1Search.trim().toLowerCase();
    const cat = STEP1_CATEGORIES.find((c) => c.key === step1Filter);
    const filteredTypes = fieldTypes.filter((ft) => {
      if (cat && cat.keys && !cat.keys.includes(ft.key)) return false;
      if (q && !ft.label.toLowerCase().includes(q) && !(ft.description || '').toLowerCase().includes(q)) return false;
      return true;
    });

    return (
      <div className="flex flex-col h-full">
        <PanelHeader
          onBack={() => setMode(null)}
          title="Add a Field"
          subtitle="Step 1 of 2 — Choose a field type"
        />

        <div className="px-5 pt-3 pb-2 space-y-2.5" style={{ background: '#f4f5fb' }}>
          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(15,15,25,0.35)' }}>
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              value={step1Search}
              onChange={(e) => setStep1Search(e.target.value)}
              placeholder={`Search ${fieldTypes.length} field types…`}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-lg outline-none"
              style={{ background: 'white', border: '1px solid rgba(99,102,241,0.15)', color: 'rgba(15,15,25,0.8)' }}
            />
          </div>

          {/* Category pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {STEP1_CATEGORIES.map((cat) => {
              const active = step1Filter === cat.key;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setStep1Filter(cat.key)}
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all duration-150 whitespace-nowrap"
                  style={active
                    ? { background: '#4f46e5', color: 'white', border: '1px solid #4f46e5' }
                    : { background: 'white', color: 'rgba(15,15,25,0.55)', border: '1px solid rgba(99,102,241,0.18)' }
                  }
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-5" style={{ background: '#f4f5fb' }}>
          <div>
            {filteredTypes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(15,15,25,0.2)' }}>
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <p className="text-xs" style={{ color: 'rgba(15,15,25,0.35)' }}>No field types match "{step1Search}"</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {filteredTypes.map((ftype) => (
                  <button
                    key={ftype.key}
                    type="button"
                    onClick={() => openCreate(ftype)}
                    className="group flex items-center gap-2.5 p-3 rounded-xl text-left transition-all duration-150"
                    style={{ background: 'white', border: '1px solid rgba(99,102,241,0.1)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)'; e.currentTarget.style.background = 'rgba(99,102,241,0.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.1)'; e.currentTarget.style.background = 'white'; }}
                  >
                    <TypeBadge ftype={ftype} />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold leading-tight" style={{ color: 'rgba(15,15,25,0.8)' }}>
                        <FormattedMessage id={`${ftype.label}`.replaceAll(' ', '_').toUpperCase()} defaultMessage={ftype.label} />
                      </div>
                      <div className="text-[10px] leading-tight mt-0.5 line-clamp-1" style={{ color: 'rgba(15,15,25,0.4)' }}>{ftype.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {libraryFields.length > 0 && !q && step1Filter === 'all' && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(15,15,25,0.4)' }}>Reuse from Shared Fields</p>
              <div className="space-y-1">
                {libraryFields.map((field) => {
                  const ft = fieldTypes.find((f) => f.key === field.type);
                  return (
                    <div
                      key={field._id}
                      className="flex items-center justify-between px-3 py-2 rounded-xl transition-all duration-150"
                      style={{ background: 'white', border: '1px solid rgba(99,102,241,0.1)' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'; e.currentTarget.style.background = 'rgba(99,102,241,0.02)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.1)'; e.currentTarget.style.background = 'white'; }}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <TypeBadge ftype={ft || { color: '#33fff8', icon: 'custom-fields.svg', key: field.type }} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate" style={{ color: 'rgba(15,15,25,0.8)' }}>{field.name}</div>
                          <div className="text-[11px] capitalize" style={{ color: 'rgba(15,15,25,0.4)' }}>{field.type}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={!!addingFieldId}
                        onClick={() => handleAddFromLibrary(field)}
                        className="flex-shrink-0 ml-3 flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-all"
                        style={{ background: 'rgba(99,102,241,0.1)', color: '#4f46e5', border: '1px solid rgba(99,102,241,0.2)', opacity: addingFieldId && addingFieldId !== field._id ? 0.5 : 1 }}
                        onMouseEnter={e => { if (!addingFieldId) e.currentTarget.style.background = 'rgba(99,102,241,0.18)'; }}
                        onMouseLeave={e => { if (!addingFieldId) e.currentTarget.style.background = 'rgba(99,102,241,0.1)'; }}
                      >
                        {addingFieldId === field._id ? (
                          <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M21 12a9 9 0 11-6.219-8.56"/>
                          </svg>
                        ) : '+ Add'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCreateForm = () => {
    const requiresOpts = ['dropdown', 'status', 'multi-select'].includes(newFieldType?.key);
    const isDisabled = !newFieldName.trim() || !selectedProject || (requiresOpts && newFieldOptions.length === 0);
    const ft = fieldTypes.find((f) => f.key === newFieldType?.key) || newFieldType;
    const CreateTriggerIcon = ft ? (FIELD_ICONS[ft.key] || Hash) : null;

    return (
      <div className="flex flex-col h-full">
        <PanelHeader
          onBack={() => { resetCreate(); setMode('add-step1'); }}
          badge={ft && <TypeBadge ftype={ft} />}
          title="Configure Field"
          subtitle="Step 2 of 2 — Set name and options"
        />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ background: '#f4f5fb' }}>
          {/* Scope selector (R2) */}
          <div>
            <label className={labelClass} style={labelStyle}>Field Availability</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSaveToWorkspace(false)}
                className="flex flex-col items-start p-3 rounded-xl text-left transition-all"
                style={{
                  border: !saveToWorkspace ? '2px solid #6366f1' : '1px solid rgba(99,102,241,0.2)',
                  background: !saveToWorkspace ? 'rgba(99,102,241,0.06)' : 'white',
                }}
              >
                <span className="text-xs font-semibold" style={{ color: !saveToWorkspace ? '#4f46e5' : 'rgba(15,15,25,0.7)' }}>
                  This project only
                </span>
                <span className="text-[10px] mt-0.5 leading-tight" style={{ color: 'rgba(15,15,25,0.45)' }}>
                  Visible in "{selectedProject?.title || 'project'}" only
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSaveToWorkspace(true)}
                className="flex flex-col items-start p-3 rounded-xl text-left transition-all"
                style={{
                  border: saveToWorkspace ? '2px solid #6366f1' : '1px solid rgba(99,102,241,0.2)',
                  background: saveToWorkspace ? 'rgba(99,102,241,0.06)' : 'white',
                }}
              >
                <span className="text-xs font-semibold" style={{ color: saveToWorkspace ? '#4f46e5' : 'rgba(15,15,25,0.7)' }}>
                  Shared field
                </span>
                <span className="text-[10px] mt-0.5 leading-tight" style={{ color: 'rgba(15,15,25,0.45)' }}>
                  Reusable across all projects
                </span>
              </button>
            </div>
          </div>

          <div>
            <label className={labelClass} style={labelStyle}>
              Field Name <span style={{ color: 'rgba(239,68,68,0.7)' }}>*</span>
            </label>
            <input
              type="text"
              autoFocus
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !isDisabled && !isSubmitting) handleCreate(); }}
              onFocus={inputFocus}
              onBlur={inputBlur}
              placeholder={`e.g. ${ft?.label || 'My Field'}`}
              className={inputClass}
              style={inputStyle}
            />
          </div>

          <div>
            <label className={labelClass} style={labelStyle}>Field Type</label>
            <Dropdown
              menu={{
                style: { maxHeight: '50vh', overflowY: 'auto' },
                items: fieldTypes.map((item) => {
                  const DIcon = FIELD_ICONS[item.key] || Hash;
                  return {
                    key: item.key,
                    label: (
                      <span className="flex items-center gap-2">
                        <DIcon size={14} strokeWidth={1.5} color="rgba(15,15,25,0.45)" style={{ flexShrink: 0 }} />
                        {intl.formatMessage({ id: `${item.label}`.replaceAll(' ', '_').toUpperCase(), defaultMessage: item.label })}
                      </span>
                    ),
                    onClick: () => {
                      const next = fieldTypes.find((f) => f.key === item.key);
                      setNewFieldType(next);
                      setNewFieldOptions(next?.options || []);
                    },
                  };
                }),
              }}
              trigger={['click']}
              getPopupContainer={() => document.body}
            >
              <button
                type="button"
                className="w-full h-10 flex items-center justify-between px-3 text-sm font-medium rounded-xl bg-white transition-colors"
                style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.75)' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.45)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'}
              >
                <span className="flex items-center gap-2">
                  {CreateTriggerIcon && <CreateTriggerIcon size={15} strokeWidth={1.5} color="rgba(15,15,25,0.5)" />}
                  <FormattedMessage id={`${newFieldType?.key || ''}`.replaceAll(' ', '_').toUpperCase()} defaultMessage={newFieldType?.label || 'Select type'} />
                </span>
                <ChevronDown size={12} color="rgba(15,15,25,0.4)" />
              </button>
            </Dropdown>
          </div>

          {newFieldType?.key === 'rating' && (
            <div>
              <label className={labelClass} style={labelStyle}>Preview</label>
              <Rate disabled value={5} />
            </div>
          )}

          {requiresOpts && (
            <div>
              <label className={labelClass} style={labelStyle}>
                Options{' '}
                {newFieldOptions.length > 0 && (
                  <span style={{ color: 'rgba(15,15,25,0.3)', fontWeight: 400 }}>({newFieldOptions.length})</span>
                )}
              </label>
              <OptionsEditor options={newFieldOptions} setOptions={setNewFieldOptions} intl={intl} />
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-5 py-3.5" style={{ borderTop: '1px solid rgba(99,102,241,0.08)', background: 'white' }}>
          <button
            type="button"
            disabled={isDisabled || isSubmitting}
            onClick={handleCreate}
            className="w-full py-1.5 rounded-xl text-sm font-semibold text-white transition-all flex items-center justify-center gap-2"
            style={(isDisabled || isSubmitting)
              ? { background: 'rgba(15,15,25,0.08)', color: 'rgba(15,15,25,0.3)', cursor: isSubmitting ? 'wait' : 'not-allowed' }
              : { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }
            }
            onMouseEnter={e => { if (!isDisabled && !isSubmitting) e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M21 12a9 9 0 11-6.219-8.56"/>
                </svg>
                Adding…
              </>
            ) : (
              <FormattedMessage id="ADD_FIELD" defaultMessage="Add Field" />
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderEditForm = () => {
    const requiresOpts = ['dropdown', 'status', 'multi-select'].includes(editType);
    const typeChanged = editType !== editOriginalType;
    const isDisabled = !editName.trim() || (requiresOpts && editOptions.length === 0) || (typeChanged && !editTypeAcknowledged);
    const ft = fieldTypes.find((f) => f.key === editType);
    const EditTriggerIcon = ft ? (FIELD_ICONS[ft.key] || Hash) : null;
    const shared = editField && isSharedField(editField);

    if (deleteConfirm) {
      return (
        <div className="flex flex-col h-full">
          <PanelHeader
            onBack={() => setDeleteConfirm(false)}
            title="Confirm Delete"
            subtitle={editField?.name}
          />
          <div className="flex-1 flex flex-col items-center justify-center px-5 text-center">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
              </svg>
            </div>
            <p className="text-sm font-semibold mb-2" style={{ color: 'rgba(15,15,25,0.8)' }}>
              Delete "{editField?.name}"?
            </p>
            <p className="text-xs leading-relaxed max-w-[240px]" style={{ color: 'rgba(15,15,25,0.5)' }}>
              {shared
                ? 'This is a Shared Field. It will be removed from all projects using it and all task data will be lost.'
                : 'This field will be removed from this project and all task data will be lost.'}
            </p>
            <p className="text-[11px] mt-2 font-medium" style={{ color: 'rgba(239,68,68,0.7)' }}>This cannot be undone.</p>
          </div>
          <div className="flex-shrink-0 px-5 py-3.5 flex gap-2" style={{ borderTop: '1px solid rgba(99,102,241,0.08)', background: 'white' }}>
            <button
              type="button"
              onClick={() => setDeleteConfirm(false)}
              className="flex-1 py-1.5 rounded-xl text-sm font-semibold transition-colors"
              style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.6)', background: 'white' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              className="flex-1 py-1.5 rounded-xl text-sm font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 4px 12px rgba(239,68,68,0.3)' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Delete Field
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        <PanelHeader
          onBack={() => { setMode(null); setEditField(null); }}
          badge={ft && <TypeBadge ftype={ft} />}
          title="Edit Field"
          subtitle={editField?.name}
        />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ background: '#f4f5fb' }}>
          {/* Scope note */}
          {shared ? (
            <>
              <div
                role="alert"
                className="flex items-start gap-2.5 p-3 rounded-xl text-xs leading-relaxed"
                style={{ background: 'rgba(234,179,8,0.07)', border: '1px solid rgba(234,179,8,0.3)', color: 'rgba(120,90,0,0.9)' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>
                  <strong>Shared Field</strong> — changes will apply to all projects currently using this field.
                </span>
              </div>
              {(() => {
                const projectsUsing = (myProjects || []).filter((p) =>
                  (p.customFields || []).some((cf) => String(cf._id) === String(editField?._id))
                );
                return projectsUsing.length > 0 ? (
                  <div className="rounded-xl px-3 py-2.5" style={{ background: 'white', border: '1px solid rgba(99,102,241,0.12)' }}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'rgba(15,15,25,0.35)' }}>
                      Used in {projectsUsing.length} project{projectsUsing.length !== 1 ? 's' : ''}
                    </p>
                    <div className="space-y-1">
                      {projectsUsing.map((p) => (
                        <div key={p._id} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'rgba(99,102,241,0.4)' }} />
                          <span className="text-xs truncate" style={{ color: 'rgba(15,15,25,0.65)' }}>{p.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl px-3 py-2.5" style={{ background: 'white', border: '1px solid rgba(99,102,241,0.12)' }}>
                    <p className="text-[11px]" style={{ color: 'rgba(15,15,25,0.4)' }}>Not added to any project yet.</p>
                  </div>
                );
              })()}
            </>
          ) : (
            <div
              role="note"
              className="flex items-start gap-2.5 p-3 rounded-xl text-xs leading-relaxed"
              style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.25)', color: 'rgba(67,56,202,0.9)' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>
                <strong>Project Field</strong> — only visible in "{selectedProject?.title}".
              </span>
            </div>
          )}

          <div>
            <label className={labelClass} style={labelStyle}>Field Name</label>
            <input
              type="text"
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onFocus={inputFocus}
              onBlur={inputBlur}
              className={inputClass}
              style={{ ...inputStyle, height: 36 }}
            />
          </div>

          <div>
            <label className={labelClass} style={labelStyle}>Field Type</label>
            <Dropdown
              menu={{
                style: { maxHeight: '50vh', overflowY: 'auto' },
                items: fieldTypes.map((item) => {
                  const EIcon = FIELD_ICONS[item.key] || Hash;
                  return {
                    key: item.key,
                    label: (
                      <span className="flex items-center gap-2">
                        <EIcon size={14} strokeWidth={1.5} color="rgba(15,15,25,0.45)" style={{ flexShrink: 0 }} />
                        {intl.formatMessage({ id: `${item.label}`.replaceAll(' ', '_').toUpperCase(), defaultMessage: item.label })}
                      </span>
                    ),
                    onClick: () => {
                      setEditType(item.key);
                      setEditTypeAcknowledged(false);
                      const next = fieldTypes.find((f) => f.key === item.key);
                      if (next?.options && editOptions.length === 0) setEditOptions(next.options);
                    },
                  };
                }),
              }}
              trigger={['click']}
              getPopupContainer={() => document.body}
            >
              <button
                type="button"
                className="w-full h-9 flex items-center justify-between px-3 text-sm font-medium rounded-xl bg-white transition-colors"
                style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.75)' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.45)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'}
              >
                <span className="flex items-center gap-2">
                  {EditTriggerIcon && <EditTriggerIcon size={15} strokeWidth={1.5} color="rgba(15,15,25,0.5)" />}
                  <FormattedMessage id={`${editType}`.replaceAll(' ', '_').toUpperCase()} defaultMessage={editType} />
                </span>
                <ChevronDown size={12} color="rgba(15,15,25,0.4)" />
              </button>
            </Dropdown>
          </div>

          {editType !== editOriginalType && (
            <div
              className="rounded-xl p-3 space-y-2.5"
              style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.2)' }}
            >
              <div className="flex items-start gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(220,38,38,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div className="text-xs leading-relaxed" style={{ color: 'rgba(185,28,28,0.9)' }}>
                  <strong>Changing the field type will erase all existing data</strong> stored in this field across every task.
                  {shared && (
                    <span> Since this is a <strong>Shared Field</strong>, this affects all projects currently using it.</span>
                  )}
                  {' '}This cannot be undone.
                </div>
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={editTypeAcknowledged}
                  onChange={(e) => setEditTypeAcknowledged(e.target.checked)}
                  className="w-3.5 h-3.5 rounded accent-red-600 cursor-pointer flex-shrink-0"
                />
                <span className="text-xs font-medium" style={{ color: 'rgba(185,28,28,0.85)' }}>
                  I understand — existing data will be permanently lost
                </span>
              </label>
            </div>
          )}

          {requiresOpts && (
            <div>
              <label className={labelClass} style={labelStyle}>
                Options
                {editOptions.length > 0 && (
                  <span style={{ color: 'rgba(15,15,25,0.3)', fontWeight: 400 }} className="ml-1">({editOptions.length})</span>
                )}
              </label>
              <OptionsEditor options={editOptions} setOptions={setEditOptions} intl={intl} />
            </div>
          )}

          {!shared && (
            <div className="pt-2" style={{ borderTop: '1px dashed rgba(99,102,241,0.15)' }}>
              {!promoteConfirm ? (
                <button
                  type="button"
                  onClick={() => setPromoteConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{ color: 'rgba(99,102,241,0.8)', border: '1px solid rgba(99,102,241,0.25)', background: 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.06)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.45)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'; }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="17 11 12 6 7 11"/><line x1="12" y1="18" x2="12" y2="6"/>
                  </svg>
                  Promote to Shared Field
                </button>
              ) : (
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium" style={{ color: 'rgba(15,15,25,0.6)' }}>Promote this field?</span>
                    <button
                      type="button"
                      onClick={() => { setPromoteConfirm(false); handlePromote(); }}
                      className="px-3 py-1 rounded-lg text-xs font-semibold text-white transition-opacity"
                      style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setPromoteConfirm(false)}
                      className="px-3 py-1 rounded-lg text-xs font-semibold transition-colors"
                      style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.55)', background: 'white' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      No
                    </button>
                  </div>
                  <p className="flex items-center gap-1 mt-1.5 text-[10px]" style={{ color: 'rgba(161,110,0,0.85)' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    Cannot be undone — field will become shared across all projects.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 px-5 py-3.5" style={{ borderTop: '1px solid rgba(99,102,241,0.08)', background: 'white' }}>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleDelete}
              className="px-4 py-1.5 text-sm font-semibold rounded-xl transition-colors"
              style={{ color: 'rgba(239,68,68,0.8)', border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.04)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.04)'}
            >
              <FormattedMessage id="DELETE_FIELD" defaultMessage="Delete" />
            </button>
            <button
              type="button"
              disabled={isDisabled}
              onClick={handleSave}
              className="flex-1 py-1.5 rounded-xl text-sm font-semibold text-white transition-all"
              style={isDisabled
                ? { background: 'rgba(15,15,25,0.08)', color: 'rgba(15,15,25,0.3)', cursor: 'not-allowed' }
                : { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }
              }
              onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.opacity = '0.88'; }}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <FormattedMessage id="SAVE_CHANGES" defaultMessage="Save Changes" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLibrary = () => {
    if (libraryDeleteField) {
      const usageCount = getUsageCount(libraryDeleteField);
      return (
        <div className="flex flex-col h-full">
          <PanelHeader
            onBack={() => setLibraryDeleteField(null)}
            title="Confirm Delete"
            subtitle={libraryDeleteField?.name}
          />
          <div className="flex-1 flex flex-col items-center justify-center px-5 text-center">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
              </svg>
            </div>
            <p className="text-sm font-semibold mb-2" style={{ color: 'rgba(15,15,25,0.8)' }}>
              Remove "{libraryDeleteField?.name}" from Shared Fields?
            </p>
            <p className="text-xs leading-relaxed max-w-[240px]" style={{ color: 'rgba(15,15,25,0.5)' }}>
              {usageCount > 0
                ? `This field is used in ${usageCount} project${usageCount !== 1 ? 's' : ''}. It will be removed from all of them and all task data will be lost.`
                : 'This field will be permanently removed from the Shared Fields library.'}
            </p>
            <p className="text-[11px] mt-2 font-medium" style={{ color: 'rgba(239,68,68,0.7)' }}>This cannot be undone.</p>
          </div>
          <div className="flex-shrink-0 px-5 py-3.5 flex gap-2" style={{ borderTop: '1px solid rgba(99,102,241,0.08)', background: 'white' }}>
            <button
              type="button"
              onClick={() => setLibraryDeleteField(null)}
              className="flex-1 py-1.5 rounded-xl text-sm font-semibold transition-colors"
              style={{ border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(15,15,25,0.6)', background: 'white' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                dispatch(deleteWorkspaceCustomField(libraryDeleteField._id, workspace._id, () => {}, intl));
                setLibraryDeleteField(null);
              }}
              className="flex-1 py-1.5 rounded-xl text-sm font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 4px 12px rgba(239,68,68,0.3)' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Delete from Shared Fields
            </button>
          </div>
        </div>
      );
    }

    const filtered = (workspaceCustomFields || []).filter((f) =>
      f?.name?.toLowerCase().includes(librarySearch.toLowerCase()),
    );

    return (
      <div className="flex flex-col h-full">
        <PanelHeader
          onBack={() => setMode(null)}
          badge={
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
            </div>
          }
          title="Shared Fields"
          subtitle={`${workspaceCustomFields?.length || 0} shared field${workspaceCustomFields?.length !== 1 ? 's' : ''} — reusable across all projects`}
        />

        <div className="px-4 pt-2.5 pb-2 flex-shrink-0" style={{ background: 'white' }}>
          <div className="flex items-center gap-2 px-3 rounded-lg" style={{ border: '1px solid rgba(99,102,241,0.18)', background: '#f4f5fb' }}>
            <img className="size-3 opacity-40" src="/images/icons/search.svg" alt="" />
            <input
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              placeholder="Search fields…"
              className="flex-1 h-8 text-sm bg-transparent focus:outline-none"
              style={{ color: 'rgba(15,15,25,0.75)' }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-3 pt-2" style={{ background: '#f4f5fb' }}>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center py-14 text-center">
              <img src="/images/icons/custom-fields.svg" className="w-12 h-12 mb-3" style={{ opacity: 0.18 }} alt="" />
              <p className="text-sm font-semibold" style={{ color: 'rgba(15,15,25,0.55)' }}>
                {librarySearch ? 'No matching fields' : 'No shared fields yet'}
              </p>
              <p className="text-xs mt-1 max-w-[220px]" style={{ color: 'rgba(15,15,25,0.35)' }}>
                {librarySearch
                  ? 'Try a different search term.'
                  : 'When creating a field, choose "Shared field" to make it reusable across all projects.'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((field) => {
                const ft = fieldTypes.find((f) => f.key === field.type);
                const inProject = selectedProject?.customFields?.find((c) => c._id === field._id);
                const usageCount = getUsageCount(field);
                return (
                  <div
                    key={field._id}
                    className="flex items-center justify-between px-3 py-2 rounded-xl transition-all duration-150 cursor-pointer"
                    style={{ background: 'white', border: '1px solid rgba(99,102,241,0.1)' }}
                    onClick={() => openEdit(field)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'; e.currentTarget.style.background = 'rgba(99,102,241,0.02)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.1)'; e.currentTarget.style.background = 'white'; }}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <TypeBadge ftype={ft || { color: '#33fff8', icon: 'custom-fields.svg', key: field.type }} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: 'rgba(15,15,25,0.8)' }}>{field.name}</div>
                        <div className="text-[11px] capitalize" style={{ color: 'rgba(15,15,25,0.4)' }}>
                          {field.type}
                          {usageCount > 0 && (
                            <span className="ml-1.5" style={{ color: 'rgba(99,102,241,0.6)' }}>
                              · {usageCount} project{usageCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      {selectedProject ? (
                        <button
                          type="button"
                          disabled={!!inProject || !!addingFieldId}
                          onClick={(e) => { e.stopPropagation(); handleAddFromLibrary(field); }}
                          className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-all"
                          style={inProject
                            ? { background: 'rgba(15,15,25,0.06)', color: 'rgba(15,15,25,0.35)', cursor: 'default' }
                            : { background: 'rgba(99,102,241,0.1)', color: '#4f46e5', border: '1px solid rgba(99,102,241,0.2)', opacity: addingFieldId && addingFieldId !== field._id ? 0.5 : 1 }
                          }
                          onMouseEnter={e => { if (!inProject && !addingFieldId) { e.currentTarget.style.background = 'rgba(99,102,241,0.18)'; } }}
                          onMouseLeave={e => { if (!inProject && !addingFieldId) e.currentTarget.style.background = 'rgba(99,102,241,0.1)'; }}
                        >
                          {addingFieldId === field._id ? (
                            <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                              <path d="M21 12a9 9 0 11-6.219-8.56"/>
                            </svg>
                          ) : inProject ? 'Added' : '+ Add'}
                        </button>
                      ) : null}
                      <Tooltip title="Edit Field">
                        <button
                          type="button"
                          aria-label="Edit Field"
                          onClick={(e) => { e.stopPropagation(); openEdit(field); }}
                          className="flex items-center justify-center rounded-lg transition-colors p-2"
                          style={{ color: 'rgba(99,102,241,0.5)' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.color = '#4f46e5'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(99,102,241,0.5)'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      </Tooltip>
                      {isAdmin && (
                        <Tooltip title="Remove from Shared Fields">
                          <button
                            type="button"
                            aria-label="Remove from Shared Fields"
                            onClick={(e) => { e.stopPropagation(); setLibraryDeleteField(field); }}
                            className="flex items-center justify-center rounded-lg transition-colors p-2"
                            style={{ color: 'rgba(239,68,68,0.5)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.07)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                            </svg>
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAI = () => (
    <div className="flex flex-col h-full">
      <PanelHeader
        onBack={() => setMode('add-step1')}
        badge={
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
          </div>
        }
        title="AI Field Suggestions"
        subtitle="Tailored to your workspace industry"
      />

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ background: '#f4f5fb' }}>
        <div className="flex gap-2">
          <input
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') generateAI(); }}
            onFocus={inputFocus}
            onBlur={inputBlur}
            placeholder="e.g. Technology, Healthcare, Real Estate…"
            className={inputClass}
            style={inputStyle}
          />
          <button
            type="button"
            onClick={generateAI}
            className="px-5 text-white text-sm font-semibold rounded-xl transition-all flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Generate
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-14">
            <Loader isLoading newLoader />
          </div>
        ) : !fields?.length ? (
          <div className="flex flex-col items-center py-12 text-center">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(99,102,241,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </div>
            <p className="text-sm font-semibold" style={{ color: 'rgba(15,15,25,0.55)' }}>No suggestions yet</p>
            <p className="text-xs mt-1 max-w-[200px]" style={{ color: 'rgba(15,15,25,0.35)' }}>Enter your industry above and click Generate.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {fields.map((cf) => {
              const ft = fieldTypes.find((f) => f.key === cf.type);
              return (
                <button
                  key={cf._id}
                  type="button"
                  onClick={() => openCreate({ ...(ft || {}), key: cf.type, label: ft?.label || cf.type }, { name: cf.name, possibleValues: cf.possibleValues })}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all duration-150 group"
                  style={{ background: 'white', border: '1px solid rgba(99,102,241,0.1)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)'; e.currentTarget.style.background = 'rgba(99,102,241,0.02)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.1)'; e.currentTarget.style.background = 'white'; }}
                >
                  <TypeBadge ftype={ft || { color: '#33fff8', icon: 'custom-fields.svg', key: cf.type }} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: 'rgba(15,15,25,0.8)' }}>{cf.name}</div>
                    <div className="text-[11px] truncate" style={{ color: 'rgba(15,15,25,0.4)' }}>{cf.description || cf.type}</div>
                  </div>
                  <span className="text-[11px] font-medium flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#6366f1' }}>
                    Use →
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ── Modal ──────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={isModalOpen}
      onCancel={closeModal}
      closable={false}
      footer={null}
      width={820}
      centered
      styles={{
        content: {
          padding: 0,
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(15,15,25,0.2), 0 8px 32px rgba(99,102,241,0.12)',
          border: '1px solid rgba(99,102,241,0.14)',
        },
        body: { padding: 0, height: 560, overflow: 'hidden' },
        mask: { backdropFilter: 'blur(6px)', background: 'rgba(15,15,25,0.4)' },
      }}
    >
      <div className="flex h-full">

        {/* ── Left Sidebar ────────────────────────────────────────────────── */}
        <div className="w-60 flex-shrink-0 flex flex-col" style={{ borderRight: '1px solid rgba(99,102,241,0.1)', background: 'white' }}>

          {/* Modal header */}
          <div className="flex items-center justify-between px-4 py-3.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(99,102,241,0.1)', background: 'rgba(99,102,241,0.02)' }}>
            <span className="text-sm font-semibold" style={{ color: 'rgba(15,15,25,0.85)' }}>Custom Fields</span>
            <button
              onClick={closeModal}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
              style={{ color: 'rgba(15,15,25,0.35)', background: 'rgba(15,15,25,0.05)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(15,15,25,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(15,15,25,0.05)'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Project picker */}
          <div className="px-3 pt-3 pb-2.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(99,102,241,0.07)' }}>
            <label className="block text-[10px] font-bold uppercase tracking-widest mb-1.5 px-1" style={{ color: 'rgba(15,15,25,0.4)' }}>
              Project
            </label>
            {/* Trigger button */}
            <div className="relative">
              {!projectPickerOpen ? (
                <button
                  type="button"
                  onClick={() => setProjectPickerOpen(true)}
                  className="w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-sm font-medium transition-colors"
                  style={{ border: '1px solid rgba(99,102,241,0.2)', background: '#f4f5fb', color: selectedProject ? 'rgba(15,15,25,0.82)' : 'rgba(15,15,25,0.4)' }}
                >
                  {selectedProject ? (
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-5 w-5 flex-shrink-0 flex items-center justify-center rounded" style={{ background: getProjectColourPalette(selectedProject?.metadata?.colour).bg, border: `1px solid ${getProjectColourPalette(selectedProject?.metadata?.colour).border}` }}>
                        <img src="/images/icons/board.svg" className="w-3 h-3" style={{ filter: 'brightness(0) saturate(100%)', opacity: 0.55 }} alt="" />
                      </div>
                      <span className="truncate">{selectedProject.title}</span>
                    </div>
                  ) : (
                    <span>Select project…</span>
                  )}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-40 ml-2">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              ) : (
                <>
                  {/* Trigger shows while open too so layout doesn't shift */}
                  <button
                    type="button"
                    onClick={() => setProjectPickerOpen(false)}
                    className="w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-sm font-medium transition-colors"
                    style={{ border: '1px solid rgba(99,102,241,0.35)', background: '#f4f5fb', color: selectedProject ? 'rgba(15,15,25,0.82)' : 'rgba(15,15,25,0.4)' }}
                  >
                    {selectedProject ? (
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-5 w-5 flex-shrink-0 flex items-center justify-center rounded" style={{ background: getProjectColourPalette(selectedProject?.metadata?.colour).bg, border: `1px solid ${getProjectColourPalette(selectedProject?.metadata?.colour).border}` }}>
                          <img src="/images/icons/board.svg" className="w-3 h-3" style={{ filter: 'brightness(0) saturate(100%)', opacity: 0.55 }} alt="" />
                        </div>
                        <span className="truncate">{selectedProject.title}</span>
                      </div>
                    ) : (
                      <span>Select project…</span>
                    )}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-40 ml-2">
                      <polyline points="6 15 12 9 18 15"/>
                    </svg>
                  </button>
                  {/* Floating dropdown */}
                  <div
                    className="absolute left-0 right-0 rounded-xl overflow-hidden shadow-xl"
                    style={{ top: 'calc(100% + 4px)', zIndex: 50, border: '1px solid rgba(99,102,241,0.18)', background: 'white' }}
                  >
                    <div className="p-2" style={{ borderBottom: '1px solid rgba(99,102,241,0.08)' }}>
                      <div className="relative">
                        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                          type="text"
                          value={projectSearch}
                          onChange={e => setProjectSearch(e.target.value)}
                          onBlur={() => setTimeout(() => setProjectPickerOpen(false), 150)}
                          placeholder="Search projects..."
                          autoFocus
                          className="w-full pl-7 pr-3 py-1.5 text-sm rounded-lg outline-none"
                          style={{ border: '1px solid rgba(99,102,241,0.2)', background: '#f4f5fb', color: 'rgba(15,15,25,0.75)' }}
                        />
                      </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {availableProjects
                        .filter(p => p.title?.toLowerCase().includes(projectSearch.toLowerCase()))
                        .map(p => {
                          const palette = getProjectColourPalette(p?.metadata?.colour);
                          const isSelected = selectedProject?._id === p._id;
                          return (
                            <div
                              key={p._id}
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => { setSelectedProject(p); setMode(null); setEditField(null); setProjectSearch(''); setProjectPickerOpen(false); }}
                              className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
                              style={{ background: isSelected ? 'rgba(99,102,241,0.07)' : 'transparent' }}
                              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(99,102,241,0.04)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = isSelected ? 'rgba(99,102,241,0.07)' : 'transparent'; }}
                            >
                              <div className="h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-lg" style={{ background: palette.bg, border: `1px solid ${palette.border}` }}>
                                <img src="/images/icons/board.svg" className="w-4 h-4" style={{ filter: 'brightness(0) saturate(100%)', opacity: 0.55 }} alt="" />
                              </div>
                              <div className="flex items-center justify-between w-full min-w-0">
                                <div className="min-w-0">
                                  <div className="text-[13px] font-medium truncate" style={{ color: 'rgba(15,15,25,0.82)' }}>{p.title}</div>
                                  <span
                                    className="text-[10px] px-1.5 py-0.5 rounded capitalize font-medium"
                                    style={p.access === 'public'
                                      ? { background: 'rgba(16,185,129,0.1)', color: '#059669' }
                                      : { background: 'rgba(99,102,241,0.08)', color: 'rgba(99,102,241,0.7)' }}
                                  >
                                    {p.access || 'public'}
                                  </span>
                                </div>
                                {isSelected && (
                                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="#4f46e5">
                                    <path d="m2.457 8.516.969-.99 2.516 2.481 5.324-5.304.985.989-6.309 6.284z" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Fields list */}
          <div className="flex-1 overflow-y-auto py-1.5">
            {!selectedProject ? (
              <p className="text-xs text-center py-8 px-4" style={{ color: 'rgba(15,15,25,0.35)' }}>Select a project above</p>
            ) : projectFields.length === 0 ? (
              <div className="flex flex-col items-center py-8 px-4 text-center">
                <img src="/images/icons/custom-fields.svg" className="w-10 h-10 mb-2" style={{ opacity: 0.18 }} alt="" />
                <p className="text-xs" style={{ color: 'rgba(15,15,25,0.35)' }}>No fields yet</p>
              </div>
            ) : (
              <div className="px-3">
                {projectFields.map((field) => {
                  const SidebarIcon = FIELD_ICONS[field.type] || Hash;
                  const sidebarStyle = FIELD_BADGE_STYLES[field.type] || DEFAULT_BADGE;
                  const isActive = editField?._id === field._id && mode === 'edit';
                  const shared = isSharedField(field);
                  return (
                    <div
                      key={field._id}
                      className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors"
                      style={{ background: isActive ? 'rgba(99,102,241,0.06)' : undefined }}
                      onClick={() => openEdit(field)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: sidebarStyle.bg, border: `1px solid ${sidebarStyle.border}` }}>
                          <SidebarIcon size={17} color={sidebarStyle.color} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800 truncate">{field.name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <div className="text-xs text-gray-400 capitalize">{field.type}</div>
                            {shared ? (
                              <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.12)', color: '#d97706' }}>Shared</span>
                            ) : (
                              <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>Project</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Tooltip title="Edit Field">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openEdit(field); }}
                          className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-md transition-colors shrink-0"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar actions */}
          <div className="px-3 py-3 space-y-1 flex-shrink-0" style={{ borderTop: '1px solid rgba(99,102,241,0.08)' }}>
            <button
              type="button"
              disabled={!selectedProject}
              onClick={() => setMode('add-step1')}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-sm font-semibold transition-all"
              style={selectedProject
                ? { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', boxShadow: '0 2px 8px rgba(99,102,241,0.25)' }
                : { background: 'rgba(15,15,25,0.05)', color: 'rgba(15,15,25,0.3)', cursor: 'not-allowed' }
              }
              onMouseEnter={e => { if (selectedProject) e.currentTarget.style.opacity = '0.88'; }}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Field
            </button>
            <button
              type="button"
              onClick={() => { setLibrarySearch(''); setMode('library'); }}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-sm font-semibold transition-all"
              style={mode === 'library'
                ? { background: 'rgba(99,102,241,0.12)', color: '#4f46e5', border: '1px solid rgba(99,102,241,0.25)' }
                : { background: 'transparent', color: 'rgba(15,15,25,0.55)', border: '1px solid rgba(15,15,25,0.12)' }
              }
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.color = '#4f46e5'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)'; }}
              onMouseLeave={e => {
                e.currentTarget.style.background = mode === 'library' ? 'rgba(99,102,241,0.12)' : 'transparent';
                e.currentTarget.style.color = mode === 'library' ? '#4f46e5' : 'rgba(15,15,25,0.55)';
                e.currentTarget.style.borderColor = mode === 'library' ? 'rgba(99,102,241,0.25)' : 'rgba(15,15,25,0.12)';
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              Shared Fields
            </button>
            {/* <button
              type="button"
              onClick={() => setMode('ai')}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors"
              style={{ color: mode === 'ai' ? '#4f46e5' : 'rgba(15,15,25,0.5)', background: mode === 'ai' ? 'rgba(99,102,241,0.07)' : 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#4f46e5'; e.currentTarget.style.background = 'rgba(99,102,241,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = mode === 'ai' ? '#4f46e5' : 'rgba(15,15,25,0.5)'; e.currentTarget.style.background = mode === 'ai' ? 'rgba(99,102,241,0.07)' : 'transparent'; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              AI Suggestions
            </button> */}
          </div>
        </div>

        {/* ── Right Panel ──────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden" style={{ background: '#f4f5fb' }}>
          {mode === null        && renderWelcome()}
          {mode === 'add-step1' && renderStep1()}
          {mode === 'add-step2' && renderCreateForm()}
          {mode === 'edit'      && renderEditForm()}
          {mode === 'library'   && renderLibrary()}
          {mode === 'ai'        && renderAI()}
        </div>

      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  project: state.projectReducer.getProjects,
  user: state.userReducer.user,
  fields: state.aiReducer.customFieldSuggestions,
  workspace: state.workspaceReducer.currentWorkspace,
  workspaceCustomFields: state.workspaceReducer.workspaceCustomFields,
  customFields: state.projectReducer.projectCustomFields,
  myProjects: state.projectReducer.myProjects,
  modalProjectId: state.modalReducer.workspaceFieldsModal.selectedProject,
  allowedProjectIds: state.modalReducer.workspaceFieldsModal.allowedProjectIds,
});

export default connect(mapStateToProps)(WorkspaceFieldsModal);
